
import { defineComponent, reactive, ref, toRefs, nextTick, onUnmounted, watch, computed, PropType, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { TrojanService, FetchOptions, TrojanItem, RiskFetchOptions, RiskItem, computedDefaultSort } from '../loaris-trojan-remover.service'
import AllScanModal from '../modals/all-scan-modal.vue'
import LighteningScanModal from '../modals/lightening-scan-modal.vue'
import UncheckRiskModal from '../modals/uncheck-risk-modal.vue'
import { Group } from '@/views/dashboard/_type/group'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { TableDataRowState, TableState } from '@/services/table-memory-pagination'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import { useTableStatusPolling } from '@/hooks/use-table-status-polling'
import { useTableRowUpdate } from '@/hooks/use-table-row-update'
import TerminalIconName from '@/views/dashboard/_components/terminal-icon-name/terminal-icon-name.vue'
import { TerminalTypeOptions } from '@/views/dashboard/_enum/terminal-type'
import { getAuthByList, PermissionFe } from '@/services/service-permission.service'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import UnauthorizedTooltip from '@/views/dashboard/_components/unauthorized-tooltip/unauthorized-tooltip.vue'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import RemoverDetails
  from '@/views/dashboard/safe-status/loaris-trojan-remover/_components/remover-details/remover-details.vue'
import { formatMapToOptions } from '@/utils/tool'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import LoadMoreButton from '@/components/load-more-button.vue'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'

const DEFAULT_PAGE_SIZE = 10
const ModulePermissionKey = {
  [AreaType.linux]: PermissionFe.linux_base_auth,
}

function isDisabled (row) {
  return getAuthByList(row.auth_status, ModulePermissionKey[row.area_type]) !== TerminalAuthStatus.normal
}

export default defineComponent({
  components: {
    RemoverDetails,
    UnauthorizedTooltip,
    AllScanModal,
    LighteningScanModal,
    UncheckRiskModal,
    IPColumn,
    TerminalIconName,
    SearchFilterBar,
    GroupTreeSelector,
    LoadMoreButton
  },

  props: {
    viewMode: {
      type: String as PropType<TableViewModeType>,
      default: TableViewMode.preview
    },
    /** 全量抽屉由统计卡片等带参打开时合并进 queryParams（如显示范围 range） */
    initialQueryPatch: {
      type: Object as PropType<Partial<FetchOptions>>,
      default: undefined,
    },
  },

  emits: ['viewAll', 'updatePreview'],

  setup (props, { emit }) {
    const router = useRouter()
    const route = useRoute()
    const { t } = useI18n()
    const scanStatusList = computed(() => [
      t('safeProtectionModule.scanStatus0'),
      t('safeProtectionModule.scanStatus1'),
      t('safeProtectionModule.scanStatus2'),
      t('safeProtectionModule.scanStatus3'),
      t('safeProtectionModule.scanStatus4'),
      t('safeProtectionModule.scanStatus5'),
    ])
    const scanTypeList = computed(() => [
      '',
      t('safeProtectionModule.lightningScan'),
      t('safeProtectionModule.fullDiskScan'),
      t('safeProtectionModule.scanTypeDefense'),
      t('safeProtectionModule.scanTypeCustom'),
    ])
    const riskCategoryList = computed(() => [
      '',
      t('safeProtectionModule.riskCatWebSettings'),
      t('safeProtectionModule.riskCatSystemGeneral'),
      t('safeProtectionModule.riskCatSystemSecurity'),
      t('safeProtectionModule.riskCatBrowser'),
      t('safeProtectionModule.riskCatMobile'),
      t('safeProtectionModule.riskCatExtDir'),
    ])
    const RISK_ITEM_DICT = computed(() => Array.from({ length: 23 }, (_, i) => t('responseHandle.riskItem' + i)))
    const injector = useReflectiveInjector([TrojanService])
    const trojanService = injector.get(TrojanService)

    trojanService.isDisabled = isDisabled

    const removerDetailsVisible = ref(false)
    const currentRow = ref({} as TrojanItem)
    const multipleTable = ref(null)
    const DEFAULT_SORT = 'risk_count'
    const hasMore = ref(true)

    const data = reactive({
      haveScaningTer: false,
      scanModal: {
        MODAL_ALL_SCAN: false,
        MODAL_LIGHTENING_SCAN: false,
        UNCHECK_RISK_MODAL: false
      },
      detailModalContent: null,
      tableData: [] as TableDataRowState<TrojanItem>[],
      total: 0,
      loading: false,
      state: {} as TableState<TableDataRowState<TrojanItem>>,
    })

    const queryParams = reactive<FetchOptions>({
      group_id: -1,
      login_status: 0,
      system_type: undefined,
      range: Number(route.query.range) || undefined,
      scan_type: undefined,
      key_word: '',
      page: 1,
      limit: props.viewMode === TableViewMode.preview ? 10 : 20,
      sort: DEFAULT_SORT,
      asc: false,
      permission_names: [ModulePermissionKey[AreaType.linux]],
      useMemory: false,
    })

    if (
      props.viewMode === TableViewMode.full &&
      props.initialQueryPatch &&
      Object.keys(props.initialQueryPatch).length > 0
    ) {
      Object.assign(queryParams, props.initialQueryPatch)
    }

    const filterFormColumns = reactive<Partial<ObjectFormItem<FetchOptions, keyof FetchOptions>>>({
      scan_type: {
        colOpt: { span: 12 },
        label: t('safeProtectionModule.lastScanType'),
        type: FormType.Select,
        selectOpt: { clearable: true },
        dictData: formatMapToOptions({
          1: t('safeProtectionModule.lightningScan'),
          2: t('safeProtectionModule.fullDiskScan'),
          3: t('safeProtectionModule.scanTypeDefense'),
          4: t('safeProtectionModule.scanTypeCustom'),
        })
      },
      range: {
        colOpt: { span: 12 },
        label: t('safeProtectionModule.displayRange'),
        type: FormType.Select,
        selectOpt: { clearable: true },
        dictData: [
          { label: t('safeProtectionModule.virusRiskTerminals'), value: 1 }
        ]
      },
      system_type: {
        colOpt: { style: 'flex: 0 0 130px' },
        label: t('safeProtectionModule.os'),
        type: FormType.Select,
        outSearchFiledFlag:true,
        selectOpt: { clearable: true },
        dictData: TerminalTypeOptions.map(item => ({ label: item.label, value: item.value })),
        events:{
          change:handleQuery
        }
      },
      group_id: {
        colOpt: { style:'flex:0 0 150px' },
        slot: true,
        label: t('safeProtectionModule.group'),
        hiddenAllLabel: true,
        outSearchFiledFlag: true,
        events: {
          change: handleQuery,
        },
      },
    })

    // 使用表格状态轮询 hook
    const { startPolling, stopPolling } = useTableStatusPolling<TableDataRowState<TrojanItem>>({
      getTableData: () => data.tableData,
      getRowKey: (row) => row.device_id,
      shouldPoll: (row) => row.scan_status === 2,
      pollApi: (rowKeys) => {
        return trojanService.getListByServer({ device_list: rowKeys } as any) as any
      },
      updateRow: (oldRow, newRow) => ({ ...newRow, $selected: oldRow.$selected }),
      syncTargetList: [
        () => data.tableData,
        () => data.state.data.list
      ],
      interval: 10000
    })

    const defaultSort = computedDefaultSort(route.query, queryParams)
    const lastRequestLimit = ref<number>(props.viewMode === TableViewMode.preview ? 10 : 20)

    async function fetchData (options: Partial<FetchOptions>, isPageOperate: boolean = false, isLoadMore: boolean = false) {
      if (!isLoadMore && !isPageOperate) {
        queryParams.page = 1
        data.tableData = []
      }

      data.loading = true

      const fetchOptions: FetchOptions = {
        group_id: options.group_id ?? -1,
        login_status: options.login_status ?? 0,
        key_word: options.key_word ?? '',
        system_type: options.system_type || undefined,
        scan_type: options.scan_type || undefined,
        range: options.range || undefined,
        page: options.page ?? 1,
        limit: options.limit ?? (props.viewMode === TableViewMode.preview ? 10 : 20),
        sort: options.sort ?? DEFAULT_SORT,
        asc: options.asc ?? false,
        permission_names: options.permission_names ?? [ModulePermissionKey[AreaType.linux]],
        useMemory: options.useMemory ?? false,
      }

      lastRequestLimit.value = fetchOptions.limit

      try {
        await trojanService.refresh(fetchOptions, isLoadMore)
          startPolling()

      } finally {
        data.loading = false
      }
    }

    trojanService.onStateChange.subscribe(state => {
      data.state = state
      data.tableData = state.data.list
      data.total = state.data.total
      hasMore.value = data.tableData.length < data.total

      nextTick(() => {
        data.tableData.forEach(i => multipleTable.value?.toggleRowSelection(i, i.$selected))
      })
    })
    function handleCheckbox (row) {
      trojanService.mergeSelectedData(trojanService.getSelectedData(), row)
    }

   async function handleSearch(value: string) {
      queryParams.key_word = value
      queryParams.page = 1
      await nextTick()
      fetchData(queryParams)
    }

    function handleQuery(form: any) {
      Object.assign(queryParams, form)
      queryParams.page = 1
      fetchData(queryParams)
    }

    function handleReset() {
      queryParams.key_word = ''
      queryParams.scan_type = undefined
      queryParams.range = Number(route.query.range) || undefined
      queryParams.system_type = undefined
      queryParams.page = 1
      fetchData(queryParams)
    }

    function updateGroupId(groupId: number) {
      queryParams.group_id = groupId
      queryParams.useMemory = false
      trojanService.cancelSelectAll()
      queryParams.page = 1
      fetchData(queryParams)
    }

    function loadMoreData() {
      if (!hasMore.value || data.loading || props.viewMode === TableViewMode.preview) return
      queryParams.page = queryParams.page! + 1
      fetchData(queryParams, false, true)
    }

    onMounted(() => {
      fetchData(queryParams)
    })

    return {
      ...toRefs(data),
      queryParams,
      filterFormColumns,
      ScanTypeOption: formatMapToOptions({
        1: t('safeProtectionModule.lightningScan'),
        2: t('safeProtectionModule.fullDiskScan'),
        3: t('safeProtectionModule.scanTypeDefense'),
        4: t('safeProtectionModule.scanTypeCustom'),
      }),
      removerDetailsVisible,
      currentRow,
      TerminalTypeOptions,
      scanTypeList,
      scanStatusList,
      riskCategoryList,
      RISK_ITEM_DICT,
      defaultSort,
      multipleTable,
      isDisabled,
      ModulePermissionKey,
      handleCheckbox,
      getAuthByList,
      calcActionBtnDisableStatus () {
        if (data.total && !data.state.selectAll) {
          return false
        }
        return true
      },
      getListByMemory () {
        queryParams.useMemory = !queryParams.useMemory
        queryParams.page = 1
        fetchData(queryParams)
      },
      selectAll () {
        queryParams.page = 1
        queryParams.useMemory = false
        trojanService.selectAll()
        fetchData(queryParams)
      },
      selectCancel () {
        queryParams.useMemory = false
        trojanService.cancelSelectAll()
        queryParams.page = 1
        fetchData(queryParams)
      },
      parseRiskType (type: number) {
        switch (type) {
          // eslint-disable-next-line no-magic-numbers
          case 10000:
            return t('safeProtectionModule.riskCatCustomDir')
          // eslint-disable-next-line no-magic-numbers
          case 20000:
            return t('safeProtectionModule.fileSecurity')
          // eslint-disable-next-line no-magic-numbers
          case 1000000:
            return t('safeProtectionModule.activeDefense')
        }

        return riskCategoryList.value[type] || ''
      },
      handlerSort ({ prop, order }: { prop: string, order: string }) {
        if (order === 'ascending') {
          queryParams.asc = true
        } else {
          queryParams.asc = false
        }
        queryParams.sort = prop
        queryParams.page = 1
        fetchData(queryParams)
      },
      handleSelectionChange (values: TableDataRowState<TrojanItem>[], currentValue?: TableDataRowState<TrojanItem>) {
        trojanService.mergeSelectedData(values, currentValue)
      },
      refreshData () {
        fetchData(queryParams)
      },
      openModal (type: string) {
        data.scanModal[type] = true
      },
      closeVirusScanModal (type: string) {
        data.scanModal[type] = false
      },
      viewDetail (row: TrojanItem) {
        data.detailModalContent = row
        data.scanModal.UNCHECK_RISK_MODAL = true
      },
      handleRemoverDetailsShow (row: TrojanItem) {
        removerDetailsVisible.value = true
        currentRow.value = row
      },
      scanTerminal (type: number) {
        if (data.state.selectAll) {
          trojanService.scanTerminal({ execute_type: 1, device_list: [], group_id: queryParams.group_id, scan_type: type }).then(() => {
            ElMessage.success(type === 1 ? t('safeProtectionModule.virusScanLightningSuccess', { n: data.total }) : t('safeProtectionModule.virusScanFullDiskSuccess', { n: data.total }))
            queryParams.page = 1
            fetchData(queryParams)
            if (props.viewMode === TableViewMode.full) {
              emit('updatePreview')
            }
          }).catch(() => {
            ElMessage.error(type === 1 ? t('safeProtectionModule.virusScanLightningFail', { n: data.total }) : t('safeProtectionModule.virusScanFullDiskFail', { n: data.total }))
          })
        } else {
          const ids = trojanService.getSelectedData().map(item => item.device_id)
          trojanService.scanTerminal({ execute_type: 0, device_list: ids, group_id: queryParams.group_id, scan_type: type }).then(() => {
          ElMessage.success(type === 1 ? t('safeProtectionModule.virusScanLightningSuccess', { n: ids.length }) : t('safeProtectionModule.virusScanFullDiskSuccess', { n: ids.length }))
          queryParams.page = 1
          fetchData(queryParams)

            if (props.viewMode === TableViewMode.full) {
              emit('updatePreview')
            }
          }).catch(() => {
            ElMessage.error(type === 1 ? t('safeProtectionModule.virusScanLightningFail', { n: ids.length }) : t('safeProtectionModule.virusScanFullDiskFail', { n: ids.length }))
          })
        }
      },
      loadMoreData,
      hasMore,
      handleSearch,
      handleQuery,
      handleReset,
      updateGroupId,
      getList: () => fetchData(queryParams),
      TableViewMode
    }
  }
})
